import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const publicApi = axios.create({ baseURL: '/api' });

function AvailHeader({ sub }) {
  return (
    <div className="avail-header">
      <img src="/mcg-logo.png" alt="MCG Career College" className="avail-logo"
        onError={e => { e.target.onerror = null; e.target.src = '/mcg-emblem.png'; }} />
      <div className="avail-brand">MCG Career College</div>
      {sub && <div className="avail-header-sub">{sub}</div>}
    </div>
  );
}

function StateCard({ icon, iconClass, title, children }) {
  return (
    <div className="avail-page"><div className="avail-wrap">
      <AvailHeader />
      <div className="avail-card avail-state-card">
        <div className={`avail-state-icon${iconClass ? ` ${iconClass}` : ''}`}>{icon}</div>
        <h2>{title}</h2>
        <div>{children}</div>
      </div>
    </div></div>
  );
}

export default function HostAvailabilityPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [host, setHost] = useState(null);
  const [errorState, setErrorState] = useState(null);
  const [answers, setAnswers] = useState({});      // program_code -> { accepting: bool|null, slots }
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    publicApi.get(`/public/host-availability/${token}`)
      .then(res => {
        setHost(res.data);
        const init = {};
        (res.data.programs || []).forEach(p => {
          init[p.program_code] = {
            accepting: p.accepting_updated_at ? !!p.accepting_students : null,
            slots: p.accepting_capacity || 1,
          };
        });
        setAnswers(init);
        setLoading(false);
      })
      .catch(err => { setErrorState(err.response?.status === 404 ? 'not_found' : 'error'); setLoading(false); });
  }, [token]);

  const setProg = (code, patch) => setAnswers(a => ({ ...a, [code]: { ...a[code], ...patch } }));

  const submit = async () => {
    const programs = host.programs || [];
    if (programs.some(p => answers[p.program_code]?.accepting == null)) {
      setSubmitError(programs.length > 1 ? 'Please answer Yes or No for each program.' : 'Please choose Yes or No.');
      return;
    }
    setSubmitting(true); setSubmitError('');
    try {
      const payload = programs.map(p => {
        const a = answers[p.program_code];
        return { program_code: p.program_code, accepting: a.accepting === true, slots: a.accepting ? a.slots : null };
      });
      const res = await publicApi.post(`/public/host-availability/${token}`, { answers: payload });
      setResult(res.data);
      setDone(true);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="avail-page"><div className="avail-wrap"><AvailHeader />
        <div className="avail-card">
          <div className="skel skel-title" style={{ width: '55%', marginBottom: 12 }} />
          <div className="skel skel-text" style={{ width: '80%', marginBottom: 24 }} />
          {[0,1].map(i => <div key={i} className="skel" style={{ width: '100%', height: 56, borderRadius: 12, marginBottom: 12 }} />)}
        </div>
      </div></div>
    );
  }

  if (errorState === 'not_found') return <StateCard icon="🔗" title="Link Not Found"><p>This link doesn't exist or has expired. Please contact MCG Career Services.</p></StateCard>;
  if (errorState) return <StateCard icon="⚠️" title="Something went wrong"><p>Unable to load the page. Please try again shortly.</p></StateCard>;

  if (done) {
    return (
      <StateCard icon="✓" iconClass="avail-state-icon--success" title="Thank you!">
        <p>We've updated availability for <strong>{host.org_name}</strong>:</p>
        <div style={{ textAlign: 'left', background: 'var(--bg-sunken, #f4f6f8)', borderRadius: 10, padding: '12px 14px', fontSize: 14, lineHeight: 1.7, marginTop: 8 }}>
          {(result.applied || []).map(a => (
            <div key={a.program_code}>
              <strong>{a.program_code}:</strong>{' '}
              {a.accepting_students
                ? `can take ${a.accepting_capacity} student${a.accepting_capacity === 1 ? '' : 's'}`
                : 'not accepting right now'}
            </div>
          ))}
        </div>
        <p style={{ marginTop: 16, fontSize: 13, color: 'var(--ink-3)' }}>Your answer has been saved — you may close this tab.</p>
      </StateCard>
    );
  }

  const programs = host.programs || [];
  if (programs.length === 0) {
    return <StateCard icon="ℹ️" title="Nothing to confirm"><p>There are no programs to update for <strong>{host.org_name}</strong> right now. Please contact MCG Career Services.</p></StateCard>;
  }
  const multi = programs.length > 1;

  return (
    <div className="avail-page"><div className="avail-wrap">
      <AvailHeader sub="Host Availability" />
      <div className="avail-card">
        <div className="avail-intro">
          <h1 className="avail-title">Can you host a practicum student?</h1>
          <p className="avail-desc">
            Hi{host.contact_first_name ? ` ${host.contact_first_name}` : ''}, we're checking in to see whether{' '}
            <strong>{host.org_name}</strong> can take on practicum students over the <strong>next 30 days</strong>
            {multi ? ', for each program below' : ''}. Your answer updates our records instantly.
          </p>
        </div>

        {programs.map(p => {
          const a = answers[p.program_code] || {};
          return (
            <div key={p.program_code} style={multi ? { border: '1px solid var(--line, #e2e6ea)', borderRadius: 12, padding: '14px 16px', marginBottom: 14 } : { marginBottom: 6 }}>
              {multi && <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: 'var(--ink, #1a2733)' }}>{p.program_name || p.program_code}</div>}
              <div className="ha-choice">
                <button type="button" className={`ha-opt${a.accepting === true ? ' yes' : ''}`} onClick={() => setProg(p.program_code, { accepting: true })}>
                  <span className="ha-opt-radio" /> Yes, we can take students
                </button>
                <button type="button" className={`ha-opt${a.accepting === false ? ' no' : ''}`} onClick={() => setProg(p.program_code, { accepting: false })}>
                  <span className="ha-opt-radio" /> Not at this time
                </button>
              </div>
              {a.accepting === true && (
                <div className="ha-slots">
                  <div className="ha-slots-label">How many students could you take?</div>
                  <div className="ha-slots-row">
                    {[1, 2, 3].map(n => (
                      <button key={n} type="button" className={`ha-chip${a.slots === n ? ' active' : ''}`} onClick={() => setProg(p.program_code, { slots: n })}>{n}</button>
                    ))}
                    <button type="button" className={`ha-chip${a.slots >= 4 ? ' active' : ''}`} onClick={() => setProg(p.program_code, { slots: a.slots >= 4 ? a.slots : 4 })}>3+</button>
                    <div className="ha-slots-num">
                      <label>or exact:</label>
                      <input type="number" min={1} max={99} value={a.slots}
                        onChange={e => setProg(p.program_code, { slots: Math.max(1, Math.min(99, parseInt(e.target.value) || 1)) })} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {submitError && <div className="avail-error">{submitError}</div>}

        <div className="avail-footer">
          <div className="avail-sel-count">{programs.length} program{programs.length === 1 ? '' : 's'}</div>
          <button className="btn btn-primary" style={{ minWidth: 160, justifyContent: 'center', padding: '11px 26px', fontSize: 14 }}
            onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </div>
      <div className="avail-page-foot">Powered by MCG Career Services Portal</div>
    </div></div>
  );
}
